import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Slider,
  Checkbox,
  FormControlLabel,
  Switch,
  Grid,
  TextField,
  Divider,
} from '@mui/material';
import {
  UploadFile as UploadIcon,
  Science as ScienceIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';

// All NudeNet v3 classes
const ALL_CLASSES = [
  'FEMALE_GENITALIA_EXPOSED',
  'FEMALE_GENITALIA_COVERED',
  'MALE_GENITALIA_EXPOSED',
  'FEMALE_BREAST_EXPOSED',
  'FEMALE_BREAST_COVERED',
  'MALE_BREAST_EXPOSED',
  'BUTTOCKS_EXPOSED',
  'BUTTOCKS_COVERED',
  'ANUS_EXPOSED',
  'ANUS_COVERED',
  'BELLY_EXPOSED',
  'BELLY_COVERED',
  'ARMPITS_EXPOSED',
  'ARMPITS_COVERED',
  'FEET_EXPOSED',
  'FEET_COVERED',
  'FACE_FEMALE',
  'FACE_MALE',
];

const DEFAULT_BLOCKED = new Set([
  'FEMALE_GENITALIA_EXPOSED',
  'MALE_GENITALIA_EXPOSED',
  'FEMALE_BREAST_EXPOSED',
  'BUTTOCKS_EXPOSED',
  'ANUS_EXPOSED',
]);

interface RawDetection {
  class: string;
  score: number;
  box: number[];
}

interface RawResult {
  success: boolean;
  image_size: { width: number; height: number };
  detection_count: number;
  detections: RawDetection[];
}

interface DecisionResult {
  pythonStatus: number;
  pythonBody: any;
  wouldBlock: boolean;
  configUsed: any;
}

const NsfwTest: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [rawResult, setRawResult] = useState<RawResult | null>(null);
  const [decisionResult, setDecisionResult] = useState<DecisionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Config knobs
  const [blockedClasses, setBlockedClasses] = useState<Set<string>>(new Set(DEFAULT_BLOCKED));
  const [defaultThreshold, setDefaultThreshold] = useState<number>(0.5);
  const [classThresholds, setClassThresholds] = useState<Record<string, string>>({});
  const [csamCheckEnabled, setCsamCheckEnabled] = useState<boolean>(true);
  const [csamAgeThreshold, setCsamAgeThreshold] = useState<number>(18);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageDisplaySize, setImageDisplaySize] = useState<{ w: number; h: number } | null>(null);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setRawResult(null);
    setDecisionResult(null);
    setError(null);
  };

  const buildConfig = () => ({
    blockedClasses: Array.from(blockedClasses),
    defaultThreshold,
    classThresholds: Object.fromEntries(
      Object.entries(classThresholds)
        .map(([k, v]) => [k, Number(v)])
        .filter(([, v]) => Number.isFinite(v as number))
    ),
    csamCheckEnabled,
    csamAgeThreshold,
  });

  const runTest = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setRawResult(null);
    setDecisionResult(null);
    try {
      // Run both in parallel
      const [rawR, decR] = await Promise.all([
        apiService.testNsfwRaw(file),
        apiService.testNsfwDecision(file, buildConfig() as any),
      ]);
      setRawResult(rawR.data as RawResult);
      setDecisionResult(decR.data as DecisionResult);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'NSFW test failed');
    } finally {
      setLoading(false);
    }
  };

  const onImageLoad = () => {
    if (imageRef.current) {
      setImageDisplaySize({
        w: imageRef.current.clientWidth,
        h: imageRef.current.clientHeight,
      });
    }
  };

  // Each detection: derive whether it would block with the current config
  const annotatedDetections = useMemo(() => {
    if (!rawResult) return [];
    return rawResult.detections.map((d) => {
      const isBlocked = blockedClasses.has(d.class);
      const perClass = Number(classThresholds[d.class]);
      const threshold = Number.isFinite(perClass) ? perClass : defaultThreshold;
      const wouldFire = isBlocked && d.score >= threshold;
      return { ...d, isBlocked, threshold, wouldFire };
    });
  }, [rawResult, blockedClasses, defaultThreshold, classThresholds]);

  const bboxStyle = (d: any): React.CSSProperties => {
    if (!rawResult || !imageDisplaySize || !d.box || d.box.length < 4) {
      return { display: 'none' };
    }
    const sx = imageDisplaySize.w / rawResult.image_size.width;
    const sy = imageDisplaySize.h / rawResult.image_size.height;
    const [x1, y1, x2, y2] = d.box;
    const color = d.wouldFire ? '#f44336' : d.isBlocked ? '#ff9800' : '#90caf9';
    return {
      position: 'absolute',
      left: x1 * sx,
      top: y1 * sy,
      width: (x2 - x1) * sx,
      height: (y2 - y1) * sy,
      border: `2px solid ${color}`,
      borderRadius: 4,
      pointerEvents: 'none',
    };
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <ScienceIcon color="primary" />
        <Typography variant="h5">NSFW Detection Test</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload any image and see exactly what NudeNet detects, then tune the
        blocked classes and thresholds to see what your production pipeline
        would decide. The image is sent to the Python service via the
        backend (PYTHON_SERVICE_URL in <code>.env</code>), so you can test
        local OR remote deployments without code changes.
      </Typography>

      <Grid container spacing={2}>
        {/* Left: image + decision */}
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                >
                  Choose image
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={onSelectFile}
                />
                <Button
                  variant="outlined"
                  startIcon={loading ? <CircularProgress size={16} /> : <ScienceIcon />}
                  onClick={runTest}
                  disabled={!file || loading}
                >
                  {loading ? 'Testing…' : 'Run test'}
                </Button>
              </Stack>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              {preview && (
                <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                  <img
                    ref={imageRef}
                    src={preview}
                    onLoad={onImageLoad}
                    alt="test"
                    style={{ maxWidth: '100%', maxHeight: 600, display: 'block', borderRadius: 8 }}
                  />
                  {annotatedDetections.map((d, i) => (
                    <Box key={i} sx={bboxStyle(d)}>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -20,
                          left: 0,
                          bgcolor: 'rgba(0,0,0,0.75)',
                          color: 'white',
                          px: 0.6,
                          fontSize: 10,
                          borderRadius: 0.5,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {d.class.replace(/_/g, ' ')} {(d.score * 100).toFixed(0)}%
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Decision result */}
          {decisionResult && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  {decisionResult.wouldBlock ? (
                    <Chip
                      icon={<BlockIcon />}
                      label={`BLOCKED — ${decisionResult.pythonBody?.error || 'rejected'}`}
                      color="error"
                    />
                  ) : (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="ALLOWED — passed moderation"
                      color="success"
                    />
                  )}
                  <Chip
                    label={`HTTP ${decisionResult.pythonStatus}`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
                {decisionResult.pythonBody?.matched_class && (
                  <Typography variant="body2" color="text.secondary">
                    Matched class:{' '}
                    <code>{decisionResult.pythonBody.matched_class}</code> @{' '}
                    {Math.round((decisionResult.pythonBody.matched_score || 0) * 100)}%
                    {decisionResult.pythonBody.min_face_age != null && (
                      <> · face age: {decisionResult.pythonBody.min_face_age}</>
                    )}
                  </Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">Raw Python response:</Typography>
                <pre
                  style={{
                    fontSize: 11,
                    overflowX: 'auto',
                    margin: 0,
                    padding: 8,
                    background: 'rgba(0,0,0,0.04)',
                    borderRadius: 4,
                  }}
                >
                  {JSON.stringify(decisionResult.pythonBody, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Raw detections */}
          {rawResult && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Raw NudeNet detections ({rawResult.detection_count})
                </Typography>
                {rawResult.detections.length === 0 ? (
                  <Alert severity="warning">
                    NudeNet returned zero detections. Either the model has a true
                    blind spot on this image, or the model isn't loaded on the
                    server.
                  </Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Class</TableCell>
                        <TableCell align="right">Score</TableCell>
                        <TableCell>Threshold</TableCell>
                        <TableCell>Would fire</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {annotatedDetections.map((d, i) => (
                        <TableRow
                          key={i}
                          sx={d.wouldFire ? { bgcolor: 'error.50' } : undefined}
                        >
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                            {d.class}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`${(d.score * 100).toFixed(1)}%`}
                              color={
                                d.score >= 0.7
                                  ? 'error'
                                  : d.score >= 0.4
                                  ? 'warning'
                                  : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {d.isBlocked ? `≥ ${(d.threshold * 100).toFixed(0)}%` : '—'}
                          </TableCell>
                          <TableCell>
                            {d.wouldFire ? (
                              <Chip size="small" label="BLOCK" color="error" />
                            ) : d.isBlocked ? (
                              <Chip size="small" label="below threshold" variant="outlined" />
                            ) : (
                              <Chip size="small" label="not blocked" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right: config */}
        <Grid item xs={12} md={5}>
          <Card sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Detection config
              </Typography>

              <Box mb={2}>
                <Typography variant="body2" gutterBottom>
                  Default threshold: <Chip size="small" label={defaultThreshold.toFixed(2)} />
                </Typography>
                <Slider
                  value={defaultThreshold}
                  onChange={(_, v) => setDefaultThreshold(Array.isArray(v) ? v[0] : v)}
                  min={0}
                  max={1}
                  step={0.05}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 0.5, label: '0.5' },
                    { value: 1, label: '1' },
                  ]}
                  size="small"
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Blocked classes + per-class threshold overrides
              </Typography>
              <Stack spacing={0.5}>
                {ALL_CLASSES.map((cls) => (
                  <Box
                    key={cls}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      p: 0.5,
                      borderRadius: 1,
                      bgcolor: blockedClasses.has(cls)
                        ? 'error.50'
                        : undefined,
                    }}
                  >
                    <FormControlLabel
                      sx={{ flex: 1, m: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={blockedClasses.has(cls)}
                          onChange={(e) => {
                            const next = new Set(blockedClasses);
                            if (e.target.checked) next.add(cls);
                            else next.delete(cls);
                            setBlockedClasses(next);
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {cls}
                        </Typography>
                      }
                    />
                    <TextField
                      size="small"
                      placeholder={`${defaultThreshold.toFixed(2)}`}
                      value={classThresholds[cls] ?? ''}
                      onChange={(e) =>
                        setClassThresholds({ ...classThresholds, [cls]: e.target.value })
                      }
                      inputProps={{
                        type: 'number',
                        min: 0,
                        max: 1,
                        step: 0.05,
                        style: { fontSize: 11, padding: '4px 6px', width: 50 },
                      }}
                      disabled={!blockedClasses.has(cls)}
                    />
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Child safety (CSAM)
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={csamCheckEnabled}
                    onChange={(e) => setCsamCheckEnabled(e.target.checked)}
                    color="error"
                  />
                }
                label={csamCheckEnabled ? 'CSAM check ON' : 'CSAM check OFF'}
              />
              <Box mt={1}>
                <Typography variant="caption">
                  CSAM age threshold:{' '}
                  <Chip size="small" label={`< ${csamAgeThreshold}`} color="error" />
                </Typography>
                <Slider
                  value={csamAgeThreshold}
                  onChange={(_, v) =>
                    setCsamAgeThreshold(Array.isArray(v) ? v[0] : v)
                  }
                  min={10}
                  max={30}
                  step={1}
                  marks={[
                    { value: 10, label: '10' },
                    { value: 18, label: '18' },
                    { value: 30, label: '30' },
                  ]}
                  size="small"
                  disabled={!csamCheckEnabled}
                />
              </Box>

              <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
                Tip: raise CSAM threshold to 25+ to test the CSAM auto-block
                path on an adult image (any face under that age plus nudity
                trips it). Lower the default threshold to 0.3 to catch
                sheer-fabric / lingerie images.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NsfwTest;
